// Procedural-body sentinel classification for classifyPosition / resolveScope.
//
// HISTORY (plan16 T10): this module used to dispatch into a per-dialect
// `ProceduralResolver` registry via a `ProceduralAttachment` attached to the
// outer `ParsedBuffer`. That middle layer was dead in production — every
// `ParsedBuffer` producer in the repo passed an empty attachments array, so
// the lookup never matched and the registry delegation was unreachable. The
// attachment types, the registry, and the per-grammar registered resolvers
// were deleted; the live procedural-locals path is the per-engine side path
// (`intellisense/resolvers/*ProceduralLocals.ts`, called from each dialect
// resolver — see Docs/ back-references).
//
// What remains is the load-bearing sentinel: when the cursor sits inside a
// `'procedural-body'` statement, the classifier must yield `'procedural-body'`
// rather than mis-running the SELECT/DML scanners over a CREATE PROCEDURE /
// BEGIN…END / $$…$$ wrapper. Both `classifyPosition` and `resolveScope`
// consume this signal.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Position Classifier —
// procedural-body hand-off.

import type { ParsedBuffer, ResolverPositionKind, SqlPosition } from '../types';

// Returns:
//  - null when the cursor is not inside a procedural-body statement (the
//    caller falls through to the SELECT/DML scanners).
//  - 'procedural-body' when the cursor is anywhere inside a procedural-body
//    statement (the outer wrapper: CREATE PROCEDURE header, AS keyword,
//    BEGIN…END body, trailing END, etc.).
//
// Return type stays `ResolverPositionKind<string> | null` to match the two
// callers' existing signatures unchanged; the only value ever produced now is
// the `'procedural-body'` core arm (or null).
export function dispatchProceduralBodyClassify(
  buffer: ParsedBuffer,
  cursor: SqlPosition,
): ResolverPositionKind<string> | null {
  const stmt = buffer.documentStructure.statementAt(cursor.offset);
  if (!stmt || stmt.kind !== 'procedural-body') return null;
  return 'procedural-body';
}
